import { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Spinner,
  HStack,
  VStack,
  Input,
  FormControl,
  FormLabel,
  Heading,
  Avatar,
  IconButton,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Select as ChakraSelect,
  Divider,
} from '@chakra-ui/react';
import { Plus, Check, X, User as UserIcon } from 'phosphor-react';
import Select from 'react-select';
import { useActionToast } from '../../services/actionToast';
import { PageTitle } from '../../components/PageTitle';
import { DataSkeleton, EmptyState } from '../../components/states';
import { isRequestFailure, useFetch } from '../../hooks/useFetch';
import AlertModal from '../../components/AlertModal';

interface User {
  id: number;
  name: string;
  email: string;
  group: {
    id: number;
    name: string;
  };
  image?: {
    url: string;
  };
}

interface Permission {
  id: number;
  code: string;
  name: string;
  description: string;
  resource: string;
  action: string;
}

interface UserPermission {
  id: number;
  permission_id: number;
  permission_code: string;
  permission_name: string;
  granted: boolean;
}

const UserPermissionsPage = () => {
  const [searchUser, setSearchUser] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [effectivePermissions, setEffectivePermissions] = useState<string[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [selectedPermission, setSelectedPermission] = useState<number | null>(null);
  const [grantAction, setGrantAction] = useState<boolean>(true);
  const toast = useActionToast();

  const {
    requestApi: searchUsers,
    data: usersData,
    loading: loadingUsers,
  } = useFetch('/v1/admin/users');

  const { requestApi: fetchUserPermissions, loading: loadingUserPermissions } = useFetch(
    '/v1/admin/permissions/users/:userId'
  );

  const { requestApi: fetchEffectivePermissions } = useFetch(
    '/v1/admin/permissions/users/:userId/effective'
  );

  const { requestApi: fetchAllPermissions } = useFetch('/v1/admin/permissions');

  const { requestApi: grantPermission, loading: granting } = useFetch(
    '/v1/admin/permissions/users/grant',
    { actionFeedback: false }
  );

  const { requestApi: revokePermission, loading: revoking } = useFetch(
    '/v1/admin/permissions/users/revoke',
    { actionFeedback: false }
  );

  const { requestApi: removePermission, loading: removing } = useFetch(
    '/v1/admin/permissions/users/:userId/:permissionId',
    { actionFeedback: false }
  );

  useEffect(() => {
    if (searchUser.length >= 3) {
      handleSearchUsers();
    }
  }, [searchUser]);

  useEffect(() => {
    if (selectedUser) {
      loadUserPermissions();
      loadEffectivePermissions();
    }
  }, [selectedUser]);

  useEffect(() => {
    loadAllPermissions();
  }, []);

  const handleSearchUsers = async () => {
    await searchUsers('get', { search: searchUser, per_page: 10 });
  };

  const loadUserPermissions = async () => {
    if (!selectedUser) return;

    const response = await fetchUserPermissions(
      'get',
      null,
      `/v1/admin/permissions/users/${selectedUser.id}`
    );

    if (response?.data?.user_permissions) {
      setUserPermissions(response.data.user_permissions);
    }
  };

  const loadEffectivePermissions = async () => {
    if (!selectedUser) return;

    const response = await fetchEffectivePermissions(
      'get',
      null,
      `/v1/admin/permissions/users/${selectedUser.id}/effective`
    );

    if (response?.data?.permissions) {
      setEffectivePermissions(response.data.permissions);
    }
  };

  const loadAllPermissions = async () => {
    const response = await fetchAllPermissions('get', { per_page: 1000 });
    if (response?.data) {
      setAvailablePermissions(response.data);
    }
  };

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setSearchUser('');
  };

  const handleAddPermission = () => {
    onOpen();
  };

  const handleSavePermission = async () => {
    if (!selectedUser || !selectedPermission) return;

    try {
      const endpoint = grantAction ? 'grant' : 'revoke';
      const apiCall = grantAction ? grantPermission : revokePermission;

      const result = await apiCall(
        'post',
        {
          user_id: selectedUser.id,
          permission_id: selectedPermission,
        },
        `/v1/admin/permissions/users/${endpoint}`
      );
      if (isRequestFailure(result)) throw result.error;

      toast({
        title: grantAction ? 'Permissão concedida' : 'Permissão revogada',
        description: `A permissão foi ${grantAction ? 'concedida' : 'revogada'} com sucesso`,
        status: 'success',
        duration: 3000,
      });

      onClose();
      loadUserPermissions();
      loadEffectivePermissions();
      setSelectedPermission(null);
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar a permissão.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleRemovePermission = async (permissionId: number) => {
    if (!selectedUser) return;

    try {
      const result = await removePermission(
        'delete',
        null,
        `/v1/admin/permissions/users/${selectedUser.id}/${permissionId}`
      );
      if (isRequestFailure(result)) throw result.error;

      toast({
        title: 'Permissão removida',
        description: 'A permissão foi removida com sucesso',
        status: 'success',
        duration: 3000,
      });

      loadUserPermissions();
      loadEffectivePermissions();
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível remover a permissão.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const permissionOptions = availablePermissions.map((p) => ({
    value: p.id,
    label: `${p.code} - ${p.name}`,
  }));

  return (
    <Box>
      <PageTitle description="Conceda ou revogue permissões específicas de cada usuário.">
        Permissões por Usuário
      </PageTitle>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Coluna 1: Busca de Usuário */}
        <Box>
          <Box bg="white" p={4} borderRadius="md" shadow="sm">
            <Box>
              <Heading size="md">Selecione um Usuário</Heading>
            </Box>
            <Box>
              <FormControl mb={4}>
                <FormLabel fontSize="sm">Buscar usuário</FormLabel>
                <Input
                  placeholder="Digite nome ou email (mín. 3 caracteres)"
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                />
              </FormControl>

              {loadingUsers && (
                <Flex justify="center" py={4}>
                  <Spinner color="blue.500" />
                </Flex>
              )}

              {!loadingUsers && usersData?.data?.length === 0 && searchUser.length >= 3 && (
                <EmptyState
                  title="Nenhum usuário encontrado"
                  description="Altere o nome ou email informado e tente novamente."
                  action={{ label: 'Limpar busca', onClick: () => setSearchUser('') }}
                />
              )}

              {usersData?.data && searchUser.length >= 3 && (
                <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
                  {usersData.data.map((user: User) => (
                    <Button
                      key={user.id}
                      variant="outline"
                      justifyContent="flex-start"
                      onClick={() => handleSelectUser(user)}
                      leftIcon={<Avatar size="sm" name={user.name} src={user.image?.url} />}
                      h="auto"
                      py={3}
                    >
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="500">{user.name}</Text>
                        <Text fontSize="xs" color="content.subtle">
                          {user.email}
                        </Text>
                        <Badge size="sm" variant="info" mt={1}>
                          {user.group.name}
                        </Badge>
                      </VStack>
                    </Button>
                  ))}
                </VStack>
              )}

              {selectedUser && (
                <>
                  <Divider my={4} />
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Flex align="center" gap={3}>
                      <Avatar name={selectedUser.name} src={selectedUser.image?.url} />
                      <Box flex={1}>
                        <Text fontWeight="600">{selectedUser.name}</Text>
                        <Text fontSize="sm" color="content.body">
                          {selectedUser.email}
                        </Text>
                        <Badge variant="info" mt={1}>
                          {selectedUser.group.name}
                        </Badge>
                      </Box>
                    </Flex>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Coluna 2: Permissões do Usuário */}
        <Box>
          {selectedUser ? (
            <Box bg="white" p={4} borderRadius="md" shadow="sm">
              <Box>
                <Flex justify="space-between" align="center">
                  <Heading size="md">Permissões</Heading>
                  <Button leftIcon={<Plus size={14} />} size="sm" onClick={handleAddPermission}>
                    Adicionar
                  </Button>
                </Flex>
              </Box>
              <Box>
                {loadingUserPermissions ? (
                  <DataSkeleton
                    variant="table"
                    rows={5}
                    columns={3}
                    label="Carregando permissões"
                  />
                ) : (
                  <Tabs colorScheme="blue">
                    <TabList>
                      <Tab>Específicas ({userPermissions.length})</Tab>
                      <Tab>Efetivas ({effectivePermissions.length})</Tab>
                    </TabList>

                    <TabPanels>
                      {/* Tab 1: Permissões Específicas */}
                      <TabPanel px={0}>
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>Permissão</Th>
                              <Th>Tipo</Th>
                              <Th width="80px">Ações</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {userPermissions.length === 0 ? (
                              <Tr>
                                <Td colSpan={3} textAlign="center" color="content.subtle">
                                  Nenhuma permissão específica
                                </Td>
                              </Tr>
                            ) : (
                              userPermissions.map((up) => (
                                <Tr key={up.id}>
                                  <Td>
                                    <VStack align="start" spacing={0}>
                                      <Text fontSize="sm" fontWeight="500">
                                        {up.permission_name}
                                      </Text>
                                      <Text fontSize="xs" color="content.subtle" fontFamily="mono">
                                        {up.permission_code}
                                      </Text>
                                    </VStack>
                                  </Td>
                                  <Td>
                                    <Badge
                                      variant={up.granted ? 'success' : 'destructive'}
                                      fontSize="xs"
                                    >
                                      {up.granted ? (
                                        <Flex align="center" gap={1}>
                                          <Check size={12} /> Concedida
                                        </Flex>
                                      ) : (
                                        <Flex align="center" gap={1}>
                                          <X size={12} /> Revogada
                                        </Flex>
                                      )}
                                    </Badge>
                                  </Td>
                                  <Td>
                                    <AlertModal
                                      alertMsg={
                                        <Text fontWeight="bold" textAlign="center" fontSize="xl">
                                          Confirma a remoção desta permissão?
                                        </Text>
                                      }
                                      msgStatus="warning"
                                      ButtonOpen={({ onClick }: any) => (
                                        <Tooltip label="Remover">
                                          <IconButton
                                            aria-label="Remover"
                                            icon={<X size={16} />}
                                            size="xs"
                                            colorScheme="red"
                                            variant="ghost"
                                            onClick={onClick}
                                            isLoading={removing}
                                          />
                                        </Tooltip>
                                      )}
                                      ConfirmButton={
                                        <Button
                                          colorScheme="red"
                                          onClick={() => handleRemovePermission(up.permission_id)}
                                          isLoading={removing}
                                        >
                                          Confirmar remoção
                                        </Button>
                                      }
                                    />
                                  </Td>
                                </Tr>
                              ))
                            )}
                          </Tbody>
                        </Table>
                      </TabPanel>

                      {/* Tab 2: Permissões Efetivas */}
                      <TabPanel px={0}>
                        <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
                          {effectivePermissions.length === 0 ? (
                            <Text textAlign="center" color="content.subtle">
                              Nenhuma permissão efetiva
                            </Text>
                          ) : (
                            effectivePermissions.map((code) => (
                              <Flex
                                key={code}
                                p={2}
                                bg="gray.50"
                                borderRadius="md"
                                fontSize="sm"
                                fontFamily="mono"
                              >
                                <Badge variant="success" mr={2}>
                                  <Check size={12} />
                                </Badge>
                                {code}
                              </Flex>
                            ))
                          )}
                        </VStack>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                )}
              </Box>
            </Box>
          ) : (
            <Box bg="white" p={4} borderRadius="md" shadow="sm">
              <Box>
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  h="400px"
                  color="content.faint"
                >
                  <UserIcon size={64} />
                  <Text fontSize="lg" mt={4}>
                    Selecione um usuário
                  </Text>
                  <Text fontSize="sm" textAlign="center" maxW="300px" mt={2}>
                    Busque e selecione um usuário à esquerda para gerenciar suas permissões
                  </Text>
                </Flex>
              </Box>
            </Box>
          )}
        </Box>
      </SimpleGrid>

      {/* Modal de Adicionar Permissão */}
      <Modal autoFocus={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Adicionar Permissão</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Permissão</FormLabel>
                <Select
                  options={permissionOptions}
                  placeholder="Selecione uma permissão"
                  onChange={(option: any) => setSelectedPermission(option?.value || null)}
                  isSearchable
                />
              </FormControl>

              <FormControl>
                <FormLabel>Ação</FormLabel>
                <ChakraSelect
                  value={grantAction ? 'grant' : 'revoke'}
                  onChange={(e) => setGrantAction(e.target.value === 'grant')}
                >
                  <option value="grant">Conceder (permite acesso)</option>
                  <option value="revoke">Revogar (bloqueia acesso)</option>
                </ChakraSelect>
              </FormControl>

              <Divider />

              <HStack justify="flex-end">
                <Button onClick={onClose} variant="outline">
                  Cancelar
                </Button>
                <Button
                  onClick={handleSavePermission}
                  isLoading={granting || revoking}
                  isDisabled={!selectedPermission}
                >
                  Salvar
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserPermissionsPage;
